/**
 * fullscreen-timeline.js
 * Runs while Theme settings > Homepage timeline > Full screen photos is on
 * (<html class="fs-on fs-mode-strip|single">).
 *
 * Every page, desktop and phones:
 *  - Envelope MENU, top left: envelope outline + incoming message blip +
 *    "MENU". Opens a mint card holding every link except CART (header menu,
 *    SEARCH, the footer menus and the socials), copied from the page so it
 *    follows the Shopify menus. One copy goes in the desktop header, one in
 *    the phone top row (where MENU was). The CSS hides the old links.
 *    A MutationObserver puts the menu back whenever the header is swapped
 *    or redrawn, so it never depends on catching a page transition event.
 * Homepage, desktop:
 *  1. Rebuilds the timeline carousel for full screen: photos at their
 *     original proportions side by side at full height with no gaps
 *     (strip), or one photo per screen that snaps. Mouse wheel, trackpad,
 *     drag and arrow keys all move it.
 *  2. Loads larger photo sizes (the section only lists up to 1200px wide).
 *  3. Tones: header on the top shade, months and year bar on the bottom
 *     shade, all white (`fixed: 'dark'`). In PLAIN_YEARS both shades are
 *     hidden and everything is black. A group without `fixed` would read the
 *     photo behind it and switch black / white on its own (TONE below).
 * Styles: assets/envelope-menu.css, assets/fullscreen-timeline.css
 */
(function () {
  if (window.__fsTimeline) return;
  window.__fsTimeline = true;

  const root = document.documentElement;
  const mq = window.matchMedia('(min-width: 750px)');
  const menuOn = () => root.classList.contains('fs-on');
  const isOn = () => menuOn() && mq.matches && !!document.querySelector('home-timeline');
  const mode = () => (root.classList.contains('fs-mode-single') ? 'single' : 'strip');
  const DESK = '.home-timeline__desktop';

  /* Years shown with no shades and black navigation (match the year labels) */
  const PLAIN_YEARS = ['2024', '2025'];

  /* Space under the year bar now that the footer row is hidden */
  const BOTTOM_GAP = 28;

  /* Brightness (0 = black, 1 = white) of the photo behind a switching piece:
     above `light` the text goes black, below `dark` it goes white,
     in between it keeps what it had (starts black from `start` up). */
  const TONE = { light: 0.5, dark: 0.42, start: 0.46 };

  /* ── Homepage overlay pieces. `fixed: 'dark'` = on a shade, white.
        Pieces without `fixed` switch with the photo behind them. ── */
  const GROUPS = [
    { el: ['header-root .header__content > .header__item--left'], fixed: 'dark' },
    { el: ['header-root .header__content > .header__item--center'], fixed: 'dark', region: '.header__heading-logo' },
    { el: ['header-root .header__content > .header__item--right'], fixed: 'dark' },
    { el: [DESK + ' .timeline-panel .timeline-months'], fixed: 'dark', all: true },
    { el: [DESK + ' > .timeline-bar', DESK + ' > .timeline-bar__labels'], fixed: 'dark' },
  ];

  const isPlain = () => {
    const p = document.querySelector(DESK + ' .timeline-panel.is-active');
    return !!p && PLAIN_YEARS.includes((p.dataset.yearLabel || '').trim());
  };
  const fixedTone = (g, plain) => (plain ? 'light' : g.fixed);

  const nodesOf = (g) => {
    const out = [];
    g.el.forEach((sel) => {
      if (g.all) document.querySelectorAll(sel).forEach((n) => out.push(n));
      else {
        const n = document.querySelector(sel);
        if (n) out.push(n);
      }
    });
    return out;
  };

  const setTone = (node, tone) => {
    node.classList.add('fs-tone');
    if (node.dataset.fsTone === tone) return;
    node.dataset.fsTone = tone;
    node.classList.toggle('fs-on-dark', tone === 'dark');
    node.classList.toggle('fs-on-light', tone === 'light');
  };

  const activeSwiper = () => {
    const c = document.querySelector(DESK + ' .timeline-panel.is-active .timeline-carousel');
    return c && c.swiper && !c.swiper.destroyed ? c.swiper : null;
  };

  /* ── Envelope menu (every page) ── */
  const ENVELOPE_SVG =
    '<svg class="fs-menu__icon" viewBox="0 0 24 18" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true">' +
    '<rect x="0.5" y="0.5" width="23" height="17"/><path d="M0.5 0.5 12 10 23.5 0.5"/></svg>';

  /* where each copy goes: desktop header, phone top row */
  const MENU_SPOTS = [
    { sel: 'header-root .header__content > .header__item--left', id: 'FsMenuPanel' },
    { sel: 'header-root .header__mobile-top-row .header__item--left', id: 'FsMenuPanelMobile' },
  ];

  function menuLinks() {
    return [
      [
        ...document.querySelectorAll('header-root .header__desktop-nav a'),
        document.querySelector('header-root .header__search'),
      ],
      [
        ...document.querySelectorAll('footer-root .footer__item--left a'),
        ...document.querySelectorAll('footer-root .footer__item--right a'),
      ],
    ];
  }

  function makeMenu(panelId) {
    const menu = document.createElement('div');
    menu.className = 'fs-menu';
    menu.innerHTML =
      '<button type="button" class="fs-menu__toggle header__text-button" aria-expanded="false" aria-controls="' + panelId + '">' +
      ENVELOPE_SVG +
      '<span class="fs-menu__dot" aria-hidden="true"></span>' +
      '<span class="fs-menu__label header__text-button">MENU</span>' +
      '</button>' +
      '<div class="fs-menu__panel" id="' + panelId + '"><ul class="fs-menu__list" role="list"></ul></div>';

    const list = menu.querySelector('.fs-menu__list');
    menuLinks().forEach((links, i) => {
      const items = links.filter((a) => a && a.textContent.trim() && a.getAttribute('href'));
      if (!items.length) return;
      if (i > 0 && list.children.length) {
        const rule = document.createElement('li');
        rule.className = 'fs-menu__rule';
        rule.setAttribute('aria-hidden', 'true');
        list.appendChild(rule);
      }
      items.forEach((a) => {
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.href = a.getAttribute('href');
        link.className = 'header__text-button';
        link.textContent = a.textContent.trim().replace(/\s+/g, ' ');
        li.appendChild(link);
        list.appendChild(li);
      });
    });

    const socials = document.querySelectorAll('footer-root .footer__social-link');
    if (socials.length) {
      const row = document.createElement('div');
      row.className = 'fs-menu__socials';
      socials.forEach((s) => {
        const a = document.createElement('a');
        a.href = s.getAttribute('href');
        a.className = 'fs-menu__social';
        a.target = '_blank';
        a.rel = 'noopener';
        a.setAttribute('aria-label', s.getAttribute('aria-label') || 'Social');
        const svg = s.querySelector('svg');
        if (svg) a.appendChild(svg.cloneNode(true));
        row.appendChild(a);
      });
      menu.querySelector('.fs-menu__panel').appendChild(row);
    }

    menu.querySelector('.fs-menu__toggle').addEventListener('click', (e) => {
      e.stopPropagation();
      setMenu(menu, !menu.classList.contains('is-open'));
    });
    menu.querySelectorAll('.fs-menu__list a').forEach((a) => {
      a.addEventListener('click', () => setMenu(menu, false));
    });
    return menu;
  }

  function buildMenu() {
    if (!menuOn()) return;
    MENU_SPOTS.forEach((spot) => {
      const host = document.querySelector(spot.sel);
      if (!host || host.querySelector('.fs-menu')) return;
      host.prepend(makeMenu(spot.id));
    });
  }

  function setMenu(menu, open) {
    if (!menu) return;
    menu.classList.toggle('is-open', open);
    const t = menu.querySelector('.fs-menu__toggle');
    if (t) t.setAttribute('aria-expanded', String(open));
  }
  document.addEventListener('click', (e) => {
    document.querySelectorAll('.fs-menu.is-open').forEach((menu) => {
      if (!menu.contains(e.target)) setMenu(menu, false);
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('.fs-menu.is-open').forEach((menu) => {
      setMenu(menu, false);
      const t = menu.querySelector('.fs-menu__toggle');
      if (t && t.offsetParent) t.focus({ preventScroll: true });
    });
  });

  /* Safety net: whenever anything is added to or removed from the page
     (a page transition swapping the header, a script redrawing it), put the
     menu back on the next frame if it's missing. */
  let watchRaf = 0;
  new MutationObserver(() => {
    if (watchRaf || !menuOn()) return;
    watchRaf = requestAnimationFrame(() => {
      watchRaf = 0;
      buildMenu();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* ── 1. Larger photos ── */
  const WIDTHS = [800, 1200, 1600, 2000, 2600, 3200];
  function upgradeImages(panel) {
    panel.querySelectorAll('img.media-card__image').forEach((img, i) => {
      if (img.dataset.fsUp) return;
      img.dataset.fsUp = '1';
      const src = img.getAttribute('src') || '';
      if (/width=\d+/.test(src)) {
        img.srcset = WIDTHS.map((w) => src.replace(/width=\d+/, 'width=' + w) + ' ' + w + 'w').join(', ');
      }
      if (mode() === 'single') {
        img.sizes = '100vw';
      } else {
        const card = img.closest('.media-card');
        const cls = card ? card.className : '';
        img.sizes = /landscape/.test(cls) ? '134vh' : /square/.test(cls) ? '100vh' : '75vh';
      }
      if (i < 4) img.loading = 'eager';
    });
  }
  function preloadAround(sw) {
    if (!sw || !sw.slides) return;
    for (let i = sw.activeIndex; i <= sw.activeIndex + 3; i++) {
      const s = sw.slides[i];
      const img = s && s.querySelector('img');
      if (img) img.loading = 'eager';
    }
  }

  /* ── 2. Carousel ── */
  function buildSwiper(el, index) {
    const panel = el.panels && el.panels[index];
    if (!panel) return;
    const swiperEl = panel.querySelector('.timeline-carousel');
    if (!swiperEl || !swiperEl.querySelectorAll('.swiper-slide').length) return;
    upgradeImages(panel);

    const single = mode() === 'single';
    const wheel = { forceToAxis: false, sensitivity: 1, releaseOnEdges: false };
    if (single) wheel.thresholdDelta = 14;
    const cfg = {
      slidesPerView: single ? 1 : 'auto',
      spaceBetween: 0,
      speed: single ? 900 : 600,
      grabCursor: true,
      resistanceRatio: single ? 0.35 : 0,
      keyboard: { enabled: true, onlyInViewport: true },
      mousewheel: wheel,
      watchSlidesProgress: true,
    };
    if (!single) {
      cfg.freeMode = { enabled: true, momentum: true, momentumRatio: 0.6, momentumBounce: false };
    }
    const sw = new Swiper(swiperEl, cfg);
    sw.on('setTranslate', schedule);
    sw.on('transitionEnd', schedule);
    sw.on('slideChange', () => {
      preloadAround(sw);
      schedule();
    });
    preloadAround(sw);
    el.desktopSwipers[index] = sw;
    if (typeof el.initVideoObservers === 'function') el.initVideoObservers(panel);
  }

  customElements.whenDefined('home-timeline').then(() => {
    const C = customElements.get('home-timeline');
    if (!C || C.prototype.__fsPatch) return;
    const original = C.prototype.initPanelSwiper;
    C.prototype.initPanelSwiper = function (index) {
      if (!isOn() || this.isMobile) return original.call(this, index);
      if (this.desktopSwipers[index]) {
        this.desktopSwipers[index].destroy(true, true);
        delete this.desktopSwipers[index];
      }
      buildSwiper(this, index);
      setup();
      requestAnimationFrame(() => {
        setup();
        schedule();
      });
    };
    C.prototype.__fsPatch = true;

    // A carousel that was already built the old way gets rebuilt
    document.querySelectorAll('home-timeline').forEach((el) => {
      if (isOn() && el.desktopSwipers && el.desktopSwipers[el.activeYearIndex]) {
        el.initPanelSwiper(el.activeYearIndex);
      }
    });
    setup();
    schedule();
  });

  /* ── Menu on every page; measurements, edge zones and tones on the desktop homepage ── */
  function setup() {
    buildMenu();
    if (!isOn()) return;
    const desk = document.querySelector(DESK);
    if (!desk) return;

    const footer = document.querySelector('footer-root');
    const header = document.querySelector('header-root .header-container');
    const hc = document.querySelector('header-root .header__content');
    const bar = desk.querySelector(':scope > .timeline-bar');
    const labels = desk.querySelector(':scope > .timeline-bar__labels');
    const months = desk.querySelector('.timeline-panel.is-active .timeline-months');

    const headerH = header ? header.offsetHeight : 124;
    const footerH = Math.max(footer ? footer.offsetHeight : 0, BOTTOM_GAP);
    const barStack = bar && labels ? bar.offsetHeight + labels.offsetHeight : 70;
    const monthsH = months ? months.offsetHeight : 0;
    root.style.setProperty('--fs-footer-h', footerH + 'px');
    root.style.setProperty('--fs-header-h', headerH + 'px');
    if (hc) root.style.setProperty('--fs-side', getComputedStyle(hc).paddingLeft);
    desk.style.setProperty('--fs-bar-stack', barStack + 'px');
    desk.style.setProperty('--fs-bottom-stack', footerH + barStack + monthsH + 4 + 'px');

    if (bar) {
      const dots = bar.querySelectorAll('.dot-pagination__dot');
      if (dots.length > 1) {
        const a = dots[0].getBoundingClientRect();
        const b = dots[1].getBoundingClientRect();
        bar.style.setProperty('--fs-dot-gap', Math.max(0, b.left - a.right) + 'px');
        bar.classList.add('fs-gap-ready');
      }
    }

    if (!desk.querySelector('.fs-edge')) {
      [['prev', 'Previous photo'], ['next', 'Next photo']].forEach(([dir, label]) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'fs-edge fs-edge--' + dir;
        btn.setAttribute('aria-label', label);
        btn.addEventListener('click', () => {
          const sw = activeSwiper();
          if (sw) dir === 'prev' ? sw.slidePrev() : sw.slideNext();
        });
        desk.appendChild(btn);
      });
    }

    const plain = isPlain();
    desk.classList.toggle('fs-plain', plain);
    GROUPS.forEach((g) => {
      nodesOf(g).forEach((node) => {
        if (g.fixed) setTone(node, fixedTone(g, plain));
        else node.classList.add('fs-tone');
      });
    });
  }

  /* ── 3. Brightness behind each switching piece (desktop homepage) ── */
  let canvas = null;
  let ctx = null;
  let raf = 0;
  let last = 0;
  let timer = 0;
  let videoTimer = 0;
  let skipVideo = false;

  function schedule() {
    if (raf || !isOn()) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      const wait = 90 - (performance.now() - last);
      if (wait > 0) {
        clearTimeout(timer);
        timer = setTimeout(schedule, wait);
        return;
      }
      last = performance.now();
      sample();
    });
  }

  const pct = (v, d) => (v && v.trim().endsWith('%') ? parseFloat(v) / 100 : d);

  function paint(W, H, s, vw, vh) {
    ctx.fillStyle = 'rgb(249, 246, 239)';
    ctx.fillRect(0, 0, W, H);
    let drewVideo = false;
    const panel = document.querySelector(DESK + ' .timeline-panel.is-active');
    if (!panel) return drewVideo;
    panel.querySelectorAll('.media-card').forEach((card) => {
      const m = card.querySelector('.media-card__image, .media-card__video');
      if (!m) return;
      const r = m.getBoundingClientRect();
      if (r.right <= 0 || r.left >= vw || r.bottom <= 0 || r.top >= vh || !r.width) return;
      let nw;
      let nh;
      if (m.tagName === 'VIDEO') {
        if (skipVideo || m.readyState < 2) return;
        nw = m.videoWidth;
        nh = m.videoHeight;
        drewVideo = true;
      } else {
        if (!m.complete || !m.naturalWidth) return;
        nw = m.naturalWidth;
        nh = m.naturalHeight;
      }
      if (!nw || !nh) return;
      const sc = Math.max(r.width / nw, r.height / nh);
      const dw = nw * sc;
      const dh = nh * sc;
      const pos = getComputedStyle(m).objectPosition.split(' ');
      const dx = r.left + (r.width - dw) * pct(pos[0], 0.5);
      const dy = r.top + (r.height - dh) * pct(pos[1], 0.5);
      ctx.save();
      ctx.beginPath();
      ctx.rect(r.left * s, r.top * s, r.width * s, r.height * s);
      ctx.clip();
      try {
        ctx.drawImage(m, dx * s, dy * s, dw * s, dh * s);
      } catch (e) {}
      ctx.restore();
    });
    return drewVideo;
  }

  function regionOf(g) {
    let box = null;
    const add = (r) => {
      if (!r || !r.width || !r.height) return;
      box = box
        ? { l: Math.min(box.l, r.left), t: Math.min(box.t, r.top), r: Math.max(box.r, r.right), b: Math.max(box.b, r.bottom) }
        : { l: r.left, t: r.top, r: r.right, b: r.bottom };
    };
    nodesOf(g).forEach((node) => {
      if (g.region) {
        const inner = node.querySelector(g.region);
        if (inner) add(inner.getBoundingClientRect());
        return;
      }
      const kids = node.querySelectorAll('a, button, span, svg');
      if (kids.length) kids.forEach((k) => add(k.getBoundingClientRect()));
      else add(node.getBoundingClientRect());
    });
    return box;
  }

  function sample() {
    if (!isOn()) return;
    const plain = isPlain();
    const desk = document.querySelector(DESK);
    if (desk) desk.classList.toggle('fs-plain', plain);
    // nothing to read when every piece has a set tone
    if (plain || GROUPS.every((g) => g.fixed)) {
      GROUPS.forEach((g) => nodesOf(g).forEach((node) => setTone(node, plain ? 'light' : g.fixed)));
      return;
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const W = 160;
    const H = Math.max(1, Math.round((W * vh) / vw));
    const s = W / vw;
    if (!canvas) {
      canvas = document.createElement('canvas');
      ctx = canvas.getContext('2d', { willReadFrequently: true });
    }
    canvas.width = W;
    canvas.height = H;

    let drewVideo = paint(W, H, s, vw, vh);
    let data;
    try {
      data = ctx.getImageData(0, 0, W, H).data;
    } catch (e) {
      // a video from another domain blocks reading pixels; leave videos out
      if (skipVideo) return;
      skipVideo = true;
      drewVideo = paint(W, H, s, vw, vh);
      try {
        data = ctx.getImageData(0, 0, W, H).data;
      } catch (e2) {
        return;
      }
    }

    GROUPS.forEach((g) => {
      if (g.fixed) {
        nodesOf(g).forEach((node) => setTone(node, g.fixed));
        return;
      }
      const box = regionOf(g);
      if (!box) return;
      const pad = 6;
      const x0 = Math.max(0, Math.floor((box.l - pad) * s));
      const x1 = Math.min(W - 1, Math.ceil((box.r + pad) * s));
      const y0 = Math.max(0, Math.floor((box.t - pad) * s));
      const y1 = Math.min(H - 1, Math.ceil((box.b + pad) * s));
      let sum = 0;
      let n = 0;
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const i = (y * W + x) * 4;
          sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
          n++;
        }
      }
      if (!n) return;
      const L = sum / n / 255;
      nodesOf(g).forEach((node) => {
        const prev = node.dataset.fsTone;
        let tone;
        if (L > TONE.light) tone = 'light';
        else if (L < TONE.dark) tone = 'dark';
        else tone = prev || (L >= TONE.start ? 'light' : 'dark');
        node.dataset.fsL = L.toFixed(2);
        setTone(node, tone);
      });
    });

    clearTimeout(videoTimer);
    if (drewVideo) videoTimer = setTimeout(schedule, 800);
  }

  /* ── Re-check when photos load, the window changes, or after a page transition ── */
  const fromTimeline = (t) => t && t.closest && t.closest(DESK);
  document.addEventListener('load', (e) => { if (fromTimeline(e.target)) schedule(); }, true);
  document.addEventListener('loadeddata', (e) => { if (fromTimeline(e.target)) schedule(); }, true);
  // year dots and arrows: update right away (also covers years with no slides)
  document.addEventListener('click', (e) => {
    if (e.target.closest && e.target.closest(DESK + ' .timeline-bar')) requestAnimationFrame(() => { setup(); schedule(); });
  });
  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      setup();
      schedule();
    }, 120);
  });
  document.addEventListener('DOMContentLoaded', setup);
  window.addEventListener('load', () => {
    setup();
    schedule();
  });
  // back / forward restores from the browser cache
  window.addEventListener('pageshow', () => {
    setup();
    schedule();
  });
  window.addEventListener('page:loaded', () => {
    setup();
    requestAnimationFrame(() => {
      setup();
      schedule();
    });
  });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { setup(); schedule(); });
  setup();
  schedule();
})();
